import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type TableRow = Record<string, unknown> & {
  __originalValues?: Record<string, string>;
  __maskedValues?: Record<string, string>;
  __metaData?: unknown;
};

interface TableSelectionContextValue {
  clearSelection: () => void;
  selectedRows?: TableRow[];
  setSelectedRows: (rows?: TableRow[]) => void;
  allRows: TableRow[];
  setAllRows: (rows: TableRow[]) => void;
}

const TableSelectionContext = createContext<
  TableSelectionContextValue | undefined
>(undefined);

export const useTableSelection = (): TableSelectionContextValue => {
  const ctx = useContext(TableSelectionContext);
  if (!ctx) {
    throw new Error(
      'useTableSelection must be used within TableSelectionProvider'
    );
  }
  return ctx;
};

export const TableSelectionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selectedRows, setSelectedRows] = useState<TableRow[]>();
  const [allRows, setAllRows] = useState<TableRow[]>([]);

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  // Memoized so the context value only changes when the selection/rows actually
  // change — otherwise every provider render re-renders all consumers.
  const value = useMemo(
    () => ({
      selectedRows,
      setSelectedRows,
      clearSelection,
      allRows,
      setAllRows,
    }),
    [selectedRows, clearSelection, allRows]
  );

  return (
    <TableSelectionContext.Provider value={value}>
      {children}
    </TableSelectionContext.Provider>
  );
};
